import { useState, useCallback, useEffect } from 'react'
import { prodexer } from '../api'
import type { Topic, Reward, EmaScore } from '../types'
import {
  useQuery,
  SearchInput,
  Spinner,
  ErrorBanner,
  SectionHeader,
  DataTable,
  LinkToCopy,
  walletUrl,
  blockUrl,
  topicUrl,
  AmountDisplay,
  formatNumber,
  truncate,
  formatTime,
} from '../components'

export function TopicExplorer({ initialQuery }: { initialQuery?: string }) {
  const topicQ = useQuery<Topic>()
  const rewardsQ = useQuery<Reward>()
  const emaQ = useQuery<EmaScore>()
  const allTopicsQ = useQuery<Topic>()

  const [hasSearched, setHasSearched] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'rewards' | 'participants'>('details')
  const [topicId, setTopicId] = useState('')

  // Fetch all topics on mount and when network changes.
  // Uses server-side pagination to fetch beyond the first 100.
  const networkKey = prodexer.networkLabel
  useEffect(() => {
    const fetchAllTopics = async () => {
      const pageSize = 100
      let offset = 0
      let all: Topic[] = []
      // Fetch pages until we get fewer than pageSize (last page) or hit 1000 (safety cap)
      while (offset < 1000) {
        const page = await prodexer.query<Topic>('topics', {
          order: [{ column: 'id', desc: false }],
          limit: pageSize,
          offset,
        })
        all = [...all, ...page]
        if (page.length < pageSize) break
        offset += pageSize
      }
      return all
    }
    allTopicsQ.execute(fetchAllTopics)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkKey])

  const search = useCallback(async (topicIdStr: string) => {
    const topicIdNum = parseInt(topicIdStr, 10)
    if (isNaN(topicIdNum)) return
    setTopicId(topicIdStr)
    setHasSearched(true)

    await Promise.all([
      topicQ.execute(() =>
        prodexer.query<Topic>('topics', {
          filters: [{ column: 'id', op: 'eq', value: String(topicIdNum) }],
          limit: 1,
        })
      ),
      rewardsQ.execute(() =>
        prodexer.query<Reward>('rewards', {
          filters: [{ column: 'topic_id', op: 'eq', value: String(topicIdNum) }],
          order: [{ column: 'block_height', desc: true }],
          limit: 100,
        })
      ),
      emaQ.execute(() =>
        prodexer.query<EmaScore>('ema_scores', {
          // is_active is a partial index predicate, not an index column.
          // The API's canUseIndex doesn't model partial predicates —
          // filtering on is_active causes "no usable index" because it's
          // not in any index's column list. Drop the is_active filter;
          // sort client-side to show active first.
          filters: [
            { column: 'topic_id', op: 'eq', value: String(topicIdNum) },
          ],
          order: [{ column: 'nonce', desc: true }],
          limit: 100,
        })
      ),
    ])
  }, [topicQ, rewardsQ, emaQ])

  // Auto-search on initialQuery (URL deep-link)
  useEffect(() => {
    if (initialQuery) {
      search(initialQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery])

  const topic = topicQ.data[0]
  const loading = topicQ.loading || rewardsQ.loading || emaQ.loading
  // Only the topic query is critical — rewards/ema failures shouldn't block the view
  const error = topicQ.error
  const rewardsError = rewardsQ.error
  const emaError = emaQ.error

  // Aggregate rewards by address
  const rewards = rewardsQ.data ?? []
  const rewardsByAddress = new Map<string, number>()
  for (const r of rewards) {
    const v = parseFloat(r.reward) || 0
    rewardsByAddress.set(r.address, (rewardsByAddress.get(r.address) ?? 0) + v)
  }

  const totalRewards = rewards.reduce((s, r) => s + (parseFloat(r.reward) || 0), 0)

  // Back to list view
  const goBack = useCallback(() => {
    setHasSearched(false)
    setTopicId('')
  }, [])

  return (
    <div>
      <SectionHeader
        title="Topic Explorer"
        subtitle="Browse topics, inspect configuration, reward distributions, and active participants."
      />

      {/* Search bar (always visible) */}
      <SearchInput
        placeholder="Topic ID (e.g. 10)"
        onSubmit={search}
        loading={loading}
        buttonText="Lookup Topic"
        currentValue={topicId}
      />

      {/* Topic detail view */}
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-4">
          <ErrorBanner error={error} />
        </div>
      )}

      {!loading && hasSearched && !error && (
        <div className="mt-6">
          {topic ? (
            <>
              {/* Back to list button */}
              <button
                onClick={goBack}
                className="mb-4 inline-flex items-center gap-1 text-sm text-text-muted hover:text-accent-text transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to topic list
              </button>

              {/* Topic metadata header */}
              {topic.metadata && (
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-text-bright">{topic.metadata}</h2>
                </div>
              )}

              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Topic ID</div>
                  <div className="mt-1 text-xl font-semibold font-mono text-accent-text">{topic.id}</div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Epoch Length</div>
                  <div className="mt-1 text-xl font-semibold font-mono text-text-secondary">
                    {formatNumber(topic.epoch_length)}
                  </div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Submission Window</div>
                  <div className="mt-1 text-xl font-semibold font-mono text-text-secondary">
                    {formatNumber(topic.worker_submission_window)}
                  </div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Ground Truth Lag</div>
                  <div className="mt-1 text-xl font-semibold font-mono text-text-secondary">
                    {formatNumber(topic.ground_truth_lag)}
                  </div>
                </div>
              </div>

              {/* Tab bar */}
              <div className="flex flex-wrap gap-1 border-b border-border-default mb-4">
                {[
                  { id: 'details' as const, label: 'Configuration' },
                  { id: 'rewards' as const, label: `Rewards (${rewards.length})` },
                  { id: 'participants' as const, label: `Active Participants (${emaQ.data?.length ?? 0})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      activeTab === tab.id
                        ? 'border-accent text-accent-text'
                        : 'border-transparent text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'details' && (
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    <DetailRow label="Creator" value={topic.creator} mono linkHref={walletUrl(topic.creator)} />
                    <DetailRow label="Loss Method" value={topic.loss_method} />
                    <DetailRow label="P Norm" value={topic.p_norm} mono />
                    <DetailRow label="C Norm" value={topic.c_norm} mono />
                    <DetailRow label="Alpha Regret" value={topic.alpha_regret} mono />
                    <DetailRow label="Epsilon" value={topic.epsilon} mono />
                    <DetailRow label="Initial Regret" value={topic.initial_regret} mono />
                    <DetailRow label="Merit Sortition Alpha" value={topic.merit_sortition_alpha} mono />
                    <DetailRow label="Active Inferer Quantile" value={topic.active_inferer_quantile} mono />
                    <DetailRow label="Active Forecaster Quantile" value={topic.active_forecaster_quantile} mono />
                    <DetailRow label="Active Reputer Quantile" value={topic.active_reputer_quantile} mono />
                    <DetailRow label="Allow Negative" value={topic.allow_negative ? 'true' : 'false'} />
                    <DetailRow label="Epoch Last Ended" value={formatNumber(topic.epoch_last_ended)} mono />
                    <DetailRow label="Created At" value={formatTime(topic.created_at)} />
                  </dl>
                </div>
              )}

              {activeTab === 'rewards' && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-emerald-300 dark:border-emerald-900/50 bg-surface p-4">
                    <div className="text-xs uppercase tracking-wider text-text-faint">
                      Total Rewards (last {rewards.length} events)
                    </div>
                    <div className="mt-1 text-2xl font-semibold font-mono text-success-text">
                      <AmountDisplay value={totalRewards} suffix=" ALLO" />
                    </div>
                  </div>

                  {rewardsByAddress.size > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-text-secondary mb-2">
                        Rewards by Address
                      </h3>
                      <DataTable
                        rows={Array.from(rewardsByAddress.entries()).map(([address, amount]) => ({ address, amount }))}
                        columns={[
                          {
                            key: 'address',
                            label: 'Address',
                            render: (r) => (
                              <LinkToCopy text={r.address} href={walletUrl(r.address)} display={<span className="text-accent-text">{truncate(r.address)}</span>} className="hover:text-accent-text" />
                            ),
                          },
                          {
                            key: 'amount',
                            label: 'Total (ALLO)',
                            render: (r) => (
                              <AmountDisplay value={r.amount} className="text-success-text" />
                            ),
                          },
                        ]}
                      />
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">Recent Reward Events</h3>
                    {rewardsError ? (
                      <ErrorBanner error={rewardsError} />
                    ) : (
                    <DataTable
                      rows={rewards}
                      emptyMessage="No rewards found for this topic"
                      columns={[
                        { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                        {
                          key: 'address',
                          label: 'Address',
                          render: (r) => (
                            <LinkToCopy text={r.address} href={walletUrl(r.address)} display={<span className="text-accent-text">{truncate(r.address)}</span>} className="hover:text-accent-text" />
                          ),
                        },
                        {
                          key: 'actor_type',
                          label: 'Actor',
                          render: (r) => (
                            <span className="text-text-muted">
                              {r.actor_type.replace('ACTOR_TYPE_', '').replace('_UNSPECIFIED', '').toLowerCase()}
                            </span>
                          ),
                        },
                        {
                          key: 'reward',
                          label: 'Reward (ALLO)',
                          render: (r) => (
                            <AmountDisplay value={r.reward} className="text-success-text" />
                          ),
                        },
                        {
                          key: 'created_at',
                          label: 'Time',
                          render: (r) => formatTime(r.created_at),
                        },
                      ]}
                    />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'participants' && (
                emaError ? (
                  <ErrorBanner error={emaError} />
                ) : (
                <DataTable
                  rows={emaQ.data ?? []}
                  emptyMessage="No active participants found"
                  columns={[
                    {
                      key: 'address',
                      label: 'Address',
                      render: (r) => (
                        <LinkToCopy text={r.address} href={walletUrl(r.address)} display={<span className="text-accent-text">{truncate(r.address)}</span>} className="hover:text-accent-text" />
                      ),
                    },
                    {
                      key: 'actor_type',
                      label: 'Actor Type',
                      render: (r) => (
                        <span className="text-text-muted">
                          {r.actor_type.replace('ACTOR_TYPE_', '').replace('_UNSPECIFIED', '').toLowerCase()}
                        </span>
                      ),
                    },
                    {
                      key: 'score',
                      label: 'EMA Score',
                      render: (r) => (
                        <span className="text-amber-text">{parseFloat(r.score).toFixed(6)}</span>
                      ),
                    },
                    { key: 'nonce', label: 'Nonce', render: (r) => formatNumber(r.nonce) },
                    { key: 'height_tx', label: 'Height TX', render: (r) => formatNumber(r.height_tx) },
                  ]}
                />
                )
              )}
            </>
          ) : (
            <div className="text-sm text-text-faint italic">Topic not found.</div>
          )}
        </div>
      )}

      {/* Topic list (default view when no search) */}
      {!hasSearched && (
        <div className="mt-6">
          {allTopicsQ.loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : allTopicsQ.error ? (
            <ErrorBanner error={allTopicsQ.error} />
          ) : (
            <DataTable
              rows={allTopicsQ.data ?? []}
              emptyMessage="No topics found"
              columns={[
                {
                  key: 'id',
                  label: 'ID',
                  render: (r) => (
                    <LinkToCopy text={String(r.id)} href={topicUrl(r.id)} display={<span className="text-accent-text font-mono">{r.id}</span>} className="hover:text-accent-text" />
                  ),
                },
                {
                  key: 'metadata',
                  label: 'Name',
                  render: (r) => (
                    <button
                      onClick={() => search(String(r.id))}
                      className="text-left text-text-secondary hover:text-accent-text hover:underline"
                    >
                      {r.metadata || `Topic ${r.id}`}
                    </button>
                  ),
                },
                {
                  key: 'loss_method',
                  label: 'Loss Method',
                  render: (r) => <span className="text-text-muted">{r.loss_method}</span>,
                },
                {
                  key: 'epoch_length',
                  label: 'Epoch',
                  render: (r) => <span className="text-text-muted">{formatNumber(r.epoch_length)}</span>,
                },
                {
                  key: 'ground_truth_lag',
                  label: 'GT Lag',
                  render: (r) => <span className="text-text-muted">{formatNumber(r.ground_truth_lag)}</span>,
                },
                {
                  key: 'created_at',
                  label: 'Created',
                  render: (r) => <span className="text-text-muted">{formatTime(r.created_at)}</span>,
                },
              ]}
            />
          )}
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value, mono, linkHref }: { label: string; value: string; mono?: boolean; linkHref?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
      <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">{label}</dt>
      {linkHref ? (
        <dd className={`text-sm text-right break-all ${mono ? 'font-mono' : ''}`}>
          <LinkToCopy text={value} href={linkHref} display={<span className="text-accent-text">{truncate(value)}</span>} className="hover:text-accent-text" />
        </dd>
      ) : (
        <dd className={`text-sm text-text-secondary text-right break-all ${mono ? 'font-mono' : ''}`}>{value}</dd>
      )}
    </div>
  )
}
